// Centers tab: single-form center creation (org + admin + password in one shot,
// like the Demos tab). Each row navigates to /admin/centers/:orgId for the
// full-width drill-down (no more cramped inline dropdown).
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Building2, Plus, Check, Copy, User, Mail, Lock, Eye, EyeOff, Users } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { useApp } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

function ExamPills({ value, onChange }) {
  const toggle = (e) => onChange(value.includes(e) ? value.filter((x) => x !== e) : [...value, e]);
  return (
    <div className="flex gap-2">
      {['celpip', 'pte'].map((e) => (
        <button key={e} type="button" onClick={() => toggle(e)}
          className={cn('rounded-md border px-3 py-1.5 text-sm font-medium',
            value.includes(e) ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-500 hover:border-slate-300')}
          data-testid={`exam-pill-${e}`}>
          {e.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

function CopyField({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <p className="mono-stat">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 truncate rounded bg-slate-100 px-2.5 py-1.5 font-mono text-sm text-slate-800" data-testid={testId}>{value}</code>
        <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* noop */ } }}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-700 hover:border-slate-500">
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default function CentersTab({ onChanged }) {
  const [orgs, setOrgs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewingAs, setViewingAs] = useState(null);
  const [result, setResult] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({
    name: '', exams: ['celpip', 'pte'], seats: '',
    admin_name: '', admin_email: '', password: '', email_credentials: false,
  });
  const navigate = useNavigate();
  const { startImpersonation } = useApp();

  const viewAs = async (orgId) => {
    setViewingAs(orgId);
    try {
      await startImpersonation(orgId);
      toast.success('You are now viewing this center as its admin.');
      navigate('/org');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not enter center view.');
    } finally { setViewingAs(null); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.adminOrgs();
      setOrgs(res.data.orgs);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to load organizations.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.admin_email.trim() || form.exams.length === 0) return;
    setBusy(true);
    try {
      const res = await api.adminCreateCenter({
        name: form.name.trim(), exams: form.exams,
        seats: form.seats ? Math.max(1, Number(form.seats)) : null,
        admin_name: form.admin_name.trim(),
        admin_email: form.admin_email.trim(),
        password: form.password.trim() || undefined,
        kind: 'paid',
        email_credentials: !!form.email_credentials,
      });
      setResult(res.data);
      setForm({ name: '', exams: ['celpip', 'pte'], seats: '', admin_name: '', admin_email: '', password: '', email_credentials: false });
      toast.success('Center created. Credentials ready to share.');
      await load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create.'); }
    setBusy(false);
  };

  const convertPaid = async (orgId) => {
    try { await api.adminConvertPaid(orgId); toast.success('Converted to paid.'); await load(); onChanged?.(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed.'); }
  };

  const expiresIn = (iso) => {
    if (!iso) return '';
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'expired';
    const h = Math.round(ms / 3600000);
    return h >= 24 ? `in ${Math.round(h / 24)}d` : `in ${h}h`;
  };

  return (
    <section className="mt-6" data-testid="centers-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg font-semibold text-slate-900">Organizations</h2>
        <Button size="sm" className="cta" onClick={() => { setCreating((c) => !c); setResult(null); }} data-testid="new-org-btn">
          <Plus className="h-3.5 w-3.5" /> {creating ? 'Cancel' : 'New organization'}
        </Button>
      </div>

      {creating && (
        <form onSubmit={create} className="mt-4 rounded-lg border border-slate-200 bg-white p-5" data-testid="new-org-form">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-slate-700" />
            <h3 className="font-display text-base font-semibold text-slate-900">Create a new center</h3>
          </div>
          <p className="mt-1 text-sm text-slate-500">One form, one submit. The center + its admin are provisioned instantly. Password is auto-generated if you leave it blank.</p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mono-stat flex items-center gap-1"><Building2 className="h-3 w-3" /> Center / organization name</label>
              <Input autoFocus value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Maple Coaching Centre" className="mt-1 h-10" data-testid="org-name" />
            </div>
            <div>
              <label className="mono-stat flex items-center gap-1"><User className="h-3 w-3" /> Admin name</label>
              <Input value={form.admin_name} onChange={(e) => setForm((f) => ({ ...f, admin_name: e.target.value }))}
                placeholder="e.g. Priya Sharma" className="mt-1 h-10" data-testid="admin-name" />
            </div>
            <div>
              <label className="mono-stat flex items-center gap-1"><Mail className="h-3 w-3" /> Admin email (this is their username)</label>
              <Input type="email" value={form.admin_email} onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))}
                placeholder="priya@maple.ca" className="mt-1 h-10" data-testid="admin-email" />
            </div>
            <div>
              <label className="mono-stat flex items-center gap-1"><Lock className="h-3 w-3" /> Initial password (WhatsApp-share)</label>
              <div className="relative mt-1">
                <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Leave blank to auto-generate" className="h-10 pr-9" data-testid="admin-password" />
                <button type="button" onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="mono-stat">Exams enabled</label>
              <div className="mt-1"><ExamPills value={form.exams} onChange={(v) => setForm((f) => ({ ...f, exams: v }))} /></div>
            </div>
            <div>
              <label className="mono-stat flex items-center gap-1"><Users className="h-3 w-3" /> Student seat cap</label>
              <Input type="number" min={1} value={form.seats} onChange={(e) => setForm((f) => ({ ...f, seats: e.target.value }))}
                placeholder="Blank = no limit" className="mt-1 h-10" data-testid="org-seats" />
            </div>
          </div>

          <label className="mt-4 flex items-start gap-2 text-sm text-slate-700" data-testid="center-email-creds">
            <input type="checkbox" checked={form.email_credentials} onChange={(e) => setForm((f) => ({ ...f, email_credentials: e.target.checked }))} className="mt-0.5" />
            <div>
              <p className="font-medium">Also email the login details to the admin</p>
              <p className="text-xs text-slate-500">Off by default. You can always copy the credentials from the confirmation card below.</p>
            </div>
          </label>

          <div className="mt-4 flex items-center gap-3">
            <Button type="submit" size="lg" className="cta" disabled={busy} data-testid="create-org-submit">
              <Plus className="h-4 w-4" /> {busy ? 'Creating…' : 'Create center'}
            </Button>
            <button type="button" className="text-sm text-slate-400 hover:text-slate-700" onClick={() => { setCreating(false); setResult(null); }}>Cancel</button>
          </div>

          {result && (
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4" data-testid="create-org-result">
              <p className="font-display text-[15px] font-semibold text-slate-900">
                {result.org.name} · created · {result.org.seats ? `${result.org.seats} student seats` : 'no seat cap'}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Share these with {result.admin.name} — the admin can sign in immediately.
                {result.email_sent && ' Login details also emailed.'}
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <CopyField label="Email" value={result.admin.email} testId="result-email" />
                <CopyField label="Password" value={result.admin.password} testId="result-password" />
              </div>
            </div>
          )}
        </form>
      )}

      {error && <p className="mt-3 border-l-2 border-red-500 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}

      {orgs.length === 0 ? (
        <p className="mt-6 text-sm text-slate-500">No organizations yet. Create your first center above.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {orgs.map((org) => (
              <div key={org.org_id} className="overflow-hidden rounded-lg border border-slate-200 bg-white transition-colors hover:border-slate-400"
                data-testid={`org-row-${org.org_id}`}>
                <div role="button" tabIndex={0}
                  onClick={() => navigate(`/admin/centers/${org.org_id}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/admin/centers/${org.org_id}`); } }}
                  className="flex w-full cursor-pointer items-center gap-3 px-5 py-3.5 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                  data-testid={`org-toggle-${org.org_id}`}>
                  <div className="grid h-9 w-9 place-items-center rounded-md bg-slate-100">
                    <Building2 className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-display text-[15px] font-semibold text-slate-900">{org.name}</p>
                      {org.kind === 'demo' && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">demo · {expiresIn(org.trial_expires_at)}</span>}
                      {org.kind === 'paid' && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">paid</span>}
                      {org.status === 'revoked' && <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-rose-700">revoked</span>}
                    </div>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {(org.exams || []).map((e) => e.toUpperCase()).join(' · ')} ·{' '}
                      {org.seats ? `${org.student_count} / ${org.seats} seats` : `${org.student_count} student${org.student_count === 1 ? '' : 's'} · no cap`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {org.kind === 'demo' && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); convertPaid(org.org_id); }}
                        className="hidden sm:inline-flex items-center gap-1 rounded-md border border-emerald-300 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                        data-testid={`convert-paid-${org.org_id}`}>
                        <Check className="h-3 w-3" /> Convert to paid
                      </button>
                    )}
                    {org.status !== 'revoked' && (
                      <button type="button"
                        onClick={(e) => { e.stopPropagation(); viewAs(org.org_id); }}
                        disabled={viewingAs === org.org_id}
                        className="hidden sm:inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white disabled:opacity-50"
                        data-testid={`view-as-${org.org_id}`}>
                        <Eye className="h-3 w-3" /> {viewingAs === org.org_id ? 'Entering…' : 'View as center'}
                      </button>
                    )}
                    <ChevronRight className="h-4 w-4 text-slate-400" />
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}
